import { ReactElement } from 'react';
import createPostingPreview from '../../views/nominal/NominalPostingPreviewForm';
import createPostedTransactions from '../../views/nominal/PostedTransactionsForm';
import { BorderlessButton } from '../../components/connectedForm/Button';
import { createBackgroundProcess } from '../../utils/BackgroundJobs';
import PurchaseInvoicePostMutation from '../../graphql/mutations/supplier/PurchaseInvoicePostMutation.graphql';
import PurchaseInvoiceCreditMutation from '../../graphql/mutations/supplier/PurchaseInvoiceCreditMutation.graphql';
import PurchaseInvoiceCreditReInvoiceMutation from '../../graphql/mutations/supplier/PurchaseInvoiceCreditReInvoiceMutation.graphql';
import PurchaseInvoiceTransactions from '../../graphql/entities/supplier/PurchaseInvoiceTransactions';
import PurchaseInvoicePostingPreview from '../../graphql/entities/supplier/PurchaseInvoicePostingPreview';
import getClientMutationId from '../../utils/getClientMutationId';
import { IFormAction, IFormActions } from '../../@types/entityTypes';
import { useInvoicePostAction } from '../useInvoicePostAction';
import { IPurchaseInvoice } from '../../graphql/schema/p4types';

export enum PurchaseInvoiceFormAction {
  PreviewPostings = 'SUPPLIER_INVOICES_PREVIEW_POSTINGS',
  ViewTransactions = 'SUPPLIER_INVOICES_VIEW_TRANSACTIONS',
  AddCreditNote = 'SUPPLIER_INVOICES_ADD_CREDIT_NOTE',
  DuplicateInvoice = 'SUPPLIER_INVOICES_DUPLICATE_INVOICE',
  PostInvoice = 'SUPPLIER_INVOICES_POST_INVOICE',
  Create = 'SUPPLIER_INVOICES_CREATE',
}

export const purchaseInvoiceFormActionsForPermissions: IFormActions<PurchaseInvoiceFormAction> = [
  {
    description: 'Preview Postings',
    actionReference: PurchaseInvoiceFormAction.PreviewPostings,
  },
  { description: 'View Transactions', actionReference: PurchaseInvoiceFormAction.ViewTransactions },
  {
    description: 'Credit & Re-Invoice',
    actionReference: PurchaseInvoiceFormAction.Create,
  },
  {
    description: 'Add Credit Note',
    actionReference: PurchaseInvoiceFormAction.AddCreditNote,
  },
  {
    description: 'Duplicate Invoice',
    actionReference: PurchaseInvoiceFormAction.DuplicateInvoice,
  },
  {
    description: 'Post Invoice',
    actionReference: PurchaseInvoiceFormAction.PostInvoice,
  },
];

const PURCHASE_INVOICE_POSTING_PREVIEW = createPostingPreview(PurchaseInvoicePostingPreview);
const PURCHASE_INVOICE_VIEW_TRANSACTIONS = createPostedTransactions(PurchaseInvoiceTransactions);

export const usePurchaseInvoiceFormActions: (
  pageView?: boolean,
  setPOMatchSlideIndex?: (index: number) => void
) => [IFormActions<PurchaseInvoiceFormAction>, ReactElement] = (pageView, setPOMatchSlideIndex) => {
  const [postInvoiceAction, modals] = useInvoicePostAction(
    PurchaseInvoicePostMutation,
    'Purchase',
    setPOMatchSlideIndex
  );

  const actions: [IFormActions<PurchaseInvoiceFormAction>, ReactElement] = [
    [
      {
        description: 'Preview Postings',
        triggerModal: {
          modalContent: PURCHASE_INVOICE_POSTING_PREVIEW,
          entityId: 'id',
          pageView: true,
          modalProps: {
            formEntityCustomUrl: 'nominal/Postings/supplier/PurchaseInvoice',
            forceReadOnly: true,
          },
        },
        performSave: true,
        saveMessage: 'Nominal/VAT Postings Preview',
        btnClass: 'btnInfo',
        individualButton: false,
        condition: displayPreview,
        actionReference: PurchaseInvoiceFormAction.PreviewPostings,
        'data-testid': 'purchase-invoice-action-preview-postings',
      },
      {
        description: 'View Transactions',
        triggerModal: {
          modalContent: PURCHASE_INVOICE_VIEW_TRANSACTIONS,
          entityId: 'id',
          pageView: true,
          modalProps: {
            formEntityCustomUrl: 'nominal/Transactions/supplier/PurchaseInvoice',
            forceReadOnly: true,
          },
        },
        btnClass: 'btnInfo',
        individualButton: false,
        condition: displayTransactions,
        actionReference: PurchaseInvoiceFormAction.ViewTransactions,
        'data-testid': 'purchase-invoice-action-view-transactions',
      },
      {
        description: 'Credit & Re-Invoice',
        fn: creditAndReInvoice,
        individualButton: false,
        condition: postedInvoice,
        component: BorderlessButton,
        alignLeft: true,
        permissionLabel: 'Supplier Invoices Create', //Need this or we accidentally rename the permission.
        actionReference: PurchaseInvoiceFormAction.Create,
        'data-testid': 'purchase-invoice-action-credit-re-invoice',
      },
      {
        description: 'Add Credit Note',
        fn: creditInvoice,
        individualButton: false,
        condition: postedInvoice,
        component: BorderlessButton,
        alignLeft: true,
        actionReference: PurchaseInvoiceFormAction.AddCreditNote,
        'data-testid': 'purchase-invoice-action-add-credit-note',
      },
      {
        description: 'Duplicate Invoice',
        fn: copyInvoice,
        individualButton: false,
        component: BorderlessButton,
        alignLeft: true,
        condition: (invoice: IPurchaseInvoice) => {
          return !((invoice && invoice.creditInvoice && invoice.documentStatus === 'DRAFT') || pageView);
        },
        actionReference: PurchaseInvoiceFormAction.DuplicateInvoice,
        'data-testid': 'purchase-invoice-action-duplicate-invoice',
      },
      postInvoiceAction as IFormAction<PurchaseInvoiceFormAction>,
    ],
    modals,
  ];
  return actions;
};

function displayPreview(invoice: IPurchaseInvoice) {
  if (invoice && invoice.documentStatus) {
    return invoice.documentStatus === 'DRAFT' || invoice.documentStatus === 'RELEASED';
  }
  return false;
}

function postedInvoice(invoice: IPurchaseInvoice) {
  if (invoice && invoice.documentStatus) {
    return invoice.documentStatus === 'POSTED';
  }
  return false;
}

function displayTransactions(invoice: IPurchaseInvoice) {
  if (invoice && invoice.documentStatus) {
    return (
      invoice.documentStatus === 'POSTED' ||
      invoice.documentStatus === 'CREDITED' ||
      invoice.documentStatus === 'REINVOICED'
    );
  }
  return false;
}

function creditInvoice(props: { values: IPurchaseInvoice } & Parameters<typeof createBackgroundProcess>[2]) {
  const { values } = props;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(PurchaseInvoiceCreditMutation, input, props, null, 'Credit Invoice', redirectCredit);
}

function creditAndReInvoice(props: { values: IPurchaseInvoice } & Parameters<typeof createBackgroundProcess>[2]) {
  const { values } = props;
  const input = {
    clientMutationId: getClientMutationId(),
    id: values.id,
  };
  createBackgroundProcess(
    PurchaseInvoiceCreditReInvoiceMutation,
    input,
    props,
    null,
    'Credit & Re-Invoice',
    redirectInvoice
  );
}

function copyInvoice(props: { values: IPurchaseInvoice } & Parameters<typeof createBackgroundProcess>[2]) {
  const { values } = props;
  const history = props.connectedForm.history;

  history.push({
    pathname: '/Suppliers/Invoice/Details/New',
    state: {
      duplicateId: values?.id,
      previousLocation: history.location?.pathname || 'back',
    },
  });
}

function redirectCredit(result: any, props: { connectedForm: any }) {
  const key = Object.keys(result)[0];
  const id = result[key].result.id;
  props.connectedForm.history.push({
    pathname: `/Suppliers/Credit/Details/${id}`,
    state: { previousLocation: props.connectedForm.history.location?.pathname || 'back' },
  });
}

function redirectInvoice(result: any, props: { connectedForm: any }) {
  const key = Object.keys(result)[0];
  const id = result[key].result.id;
  props.connectedForm.history.push({
    pathname: `/Suppliers/Invoice/Details/${id}`,
    state: { previousLocation: props.connectedForm.history.location?.pathname || 'back' },
  });
}
